<template>
    <v-modal scroll size="extra-large" title="Autopay Schedule" @close="$emit('close')">
        <div slot="contents">
            <div class="modal-content" v-if="$v.$error">
                <div class="grid-item grid-item-full">
                    <v-error-messages>
                        <li class="list-item" v-if="$v.invoice.retryInstruction.$error">
                            Please verify the scheduled attempts for missing data.
                        </li>
                        <li class="list-item" v-if="$v.invoice.autopayScheduledTime.$error">
                            The autopay time must be 10 minutes or greater in the future.
                        </li>
                    </v-error-messages>
                </div>
            </div>
            <div class="modal-content modal-content-secondary">
                <div class="grid-item">
                    <h2 class="stack-m">The next payment is scheduled to be collected automatically on {{invoice.autopayScheduledTime | dateTime}}.</h2>
                    <v-date-input
                        label="Payment will be collected automatically"
                        type="datetime"
                        v-model="autopayScheduledTime"/>
                </div>
            </div>
            <schedule-invoice-body
                @add-attempt="addAttempt"
                @delete-attempt="deleteAttempt"
                :attempts="invoice.retryInstruction.attempts"
                class="modal-content">
                <template #attempt="{attempt, index, formatter}">
                    <define-schedule-view
                        :format-type-drop-down-label="formatter"
                        :schedule-instruction="attempt.scheduleInstruction"
                        invoice-retry-schedule
                        :validate="$v.invoice.retryInstruction.attempts[index].scheduleInstruction"
                        @update="data => updateSchedule(index, data)"/>
                </template>
                <template #after-each-attempt>
                    <div class="grid-content">
                        <div
                            :key="option.value"
                            v-for="option in afterAttemptPolicies"
                            class="grid-item grid-item-full">
                            <r-checkbox
                                :value="option.value"
                                v-model="invoice.retryInstruction.afterAttemptPolicies"
                                :label="option.label"/>
                        </div>
                    </div>
                </template>
                <template #after-last-attempt>
                    <div class="grid-content">
                        <div
                            :key="option.value"
                            v-for="option in afterRetryEndPolicies"
                            class="grid-item grid-item-full">
                            <r-checkbox
                                :value="option.value"
                                v-model="invoice.retryInstruction.afterRetryEndPolicies"
                                :label="option.label"/>
                        </div>
                    </div>
                </template>
            </schedule-invoice-body>
        </div>

        <template slot="actions">
            <r-button
                type="primary"
                @click="save">Update Autopay</r-button>
        </template>
        <template slot="left-actions">
            <r-button type="danger" v-if="originalInvoice.autopayScheduledTime"
                @click="remove">Remove Autopay</r-button>
        </template>
    </v-modal>
</template>

<script>
    import InvoiceModel from '@/common/models/invoice-model';
    import vModal from '@/components/modal/v-modal';
    import {
        vErrorMessages, vDateInput,
    } from '@/components/form';
    import {required} from 'vuelidate/lib/validators';
    import ScheduleInvoiceBody from '@/modules/rules-engine/actions/schedule-invoice-retry/schedule-invoice-body';
    import DefineScheduleView from '@/modules/rules-engine/components/actions/schedule-payment/define-schedule-view';
    import ScheduleInvoiceRetryActionModel from '@/common/models/rules/actions/schedule-invoice-retry-action-model';
    import getScheduleValidation
        from '@/modules/rules-engine/components/actions/schedule-payment/get-schedule-validation';

    export default {
        components: {
            vModal,
            vErrorMessages,
            vDateInput,
            DefineScheduleView,
            ScheduleInvoiceBody,
        },
        props: {
            originalInvoice: {
                type: InvoiceModel,
            },
        },
        computed: {
            autopayScheduledTime: {
                get() {
                    return this.$tz().readUTC(this.invoice.preferableAutopayTime);
                },
                set(value) {
                    this.invoice.autopayScheduledTime = value.format();
                },
            },
            afterAttemptPolicies() {
                return ScheduleInvoiceRetryActionModel.getAfterAttemptPolicies();
            },
            afterRetryEndPolicies() {
                return ScheduleInvoiceRetryActionModel.getAfterRetryEndPolicies();
            },
        },
        data() {
            return {
                invoice: this.originalInvoice.cloneSelf(),
            };
        },
        validations() {
            const validations = {
                invoice: {
                    retryInstruction: {
                        attempts: {},
                    },
                    autopayScheduledTime: {
                        required,
                        isFutureIfSet(value) {
                            return value === null || value.valueOf() >= this.$tz().now().add(10, 'minutes').valueOf();
                        },
                    },
                },
            };

            validations.invoice.retryInstruction.attempts = this.invoice
                .retryInstruction.attempts.reduce((memo, attempt, index) => ({
                    ...memo,
                    [index]: {
                        scheduleInstruction: getScheduleValidation(attempt.scheduleInstruction),
                    },
                }), {});
            return validations;
        },
        methods: {
            save() {
                this.invoice.autopayScheduledTime = this.autopayScheduledTime;
                this.$v.$touch();
                if (!this.$v.$error) {
                    this.updateInvoice();
                }
            },
            remove() {
                this.invoice.autopayScheduledTime = null;
                this.updateInvoice();
            },
            updateInvoice() {
                this.$emit('update', this.invoice);
                this.$emit('close');
            },
            addAttempt() {
                this.invoice.retryInstruction.addAttempt();
            },
            deleteAttempt(index) {
                this.invoice.retryInstruction.deleteAttempt(index);
            },
            updateSchedule(index, scheduleInstruction) {
                this.invoice.retryInstruction.updateAttempt(index, {scheduleInstruction});
            },
        },
    };
</script>
